// CB:BEGIN admin-ops-page
/**
 * FR-ADMIN-OPS: Admin Ops — Create off-platform project, link to project overrides (ProjectCockpit).
 */
import React, { useState } from 'react';
import { useApi } from '../../lib/useApi';

interface AdminOpsPageProps {
  onNavigate?: (screen: string) => void;
}

const AdminOpsPage: React.FC<AdminOpsPageProps> = ({ onNavigate }) => {
  const api = useApi();
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState<{ id: string; address: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) {
      setError('Address is required');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        address: address.trim(),
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip: zip.trim() || undefined,
      };
      if (email || phone || firstName || lastName) {
        payload.homeownerContactPrivate = {
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        };
      }
      const data = await api.post<{ id: string; address: string; homeownerPlaceholder: boolean }>(
        '/api/admin/projects/off-platform',
        payload
      );
      setCreated({ id: data.id, address: data.address });
      setAddress('');
      setCity('');
      setState('');
      setZip('');
      setEmail('');
      setPhone('');
      setFirstName('');
      setLastName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="admin-ops-page">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">Admin Ops</h1>
      <p className="text-gray-600 mb-6">
        Create off-platform projects (contractor-only onboarding). All actions are audited. Use Project Cockpit for overrides and ledger.
      </p>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-amber-800">
          <strong>OFF-PLATFORM HOMEOWNER:</strong> Admin is acting as proxy. Homeowner contact is stored privately and never shown to contractors pre-award.
        </p>
      </div>

      <section className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Create off-platform project</h2>
        <form onSubmit={handleCreate}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                placeholder="123 Main St"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input type="text" value={state} onChange={(e) => setState(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
              <input type="text" value={zip} onChange={(e) => setZip(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2 mb-4">Private homeowner contact (admin only):</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2" />
            </div>
          </div>
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          {created && (
            <p className="text-green-600 text-sm mt-2">
              Created project: {created.address}.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => onNavigate?.('admin-project-cockpit')}
              >
                Open Project Cockpit
              </button>{' '}
              and go to project {created.id} for overrides.
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating…' : 'Create off-platform project'}
          </button>
        </form>
      </section>

      <p className="text-gray-500 text-sm">
        For status overrides, bid adjustments, offline deposit, and force release: open a project in <strong>Projects</strong> and use the Admin Overrides section.
      </p>
    </div>
  );
};

export default AdminOpsPage;
// CB:END admin-ops-page
